import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { NoSSR } from '@ui/components/NoSSR';
import { GENERIC_DISPLAY_DECIMALS } from '@ui/constants/numbersDecimals';
import { Interval } from '@ui/constants/poll';
import { useTokenPriceLatest } from '@ui/hooks/price/usePriceLatest';
import { usePayTokenBalance } from '@ui/hooks/usePayTokenBalance';
import { Field } from '@ui/lib/park/ui/field';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'next-i18next';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Flex } from 'styled-system/jsx';
import { useReadContract } from 'wagmi';
import SLVMONABI from '@ui/services/contract/abis/SLVMON';
import { Address, formatUnits, parseUnits } from 'viem';

import { ExchangeContext } from '../Context';
import { InputToken } from '../InputToken';

type Props = {};

export const UnstakeInput: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const {
    inputToken,
    outputToken,
    inputAmount,
    setInputToken,
    setInputAmount,
    setAmountMode,
    inputTokens,
  } = useContext(ExchangeContext);

  const balance = usePayTokenBalance(inputToken, Interval.FAST);
  const price = useTokenPriceLatest(outputToken);

  // Fetch conversion rate: 1 Share (sLVMON) = ? Assets (LVMON)
  const { data: oneShareInAssets } = useReadContract({
    address: inputToken?.address as Address,
    abi: SLVMONABI,
    functionName: 'convertToAssets',
    args: [parseUnits('1', inputToken?.decimals || 18)],
    query: {
      enabled: !!inputToken?.address,
    },
  });

  const rate = useMemo(() => {
    if (!oneShareInAssets) return null;
    return new BigNumber(formatUnits(oneShareInAssets, inputToken?.decimals || 18));
  }, [oneShareInAssets, inputToken]);

  const balanceInAssets = useMemo(() => {
    if (!balance || !rate) return null;
    return balance.times(rate);
  }, [balance, rate]);

  // Local state for the display value (Assets)
  const [displayValue, setDisplayValue] = useState('');

  // Sync display value when inputAmount (Shares) changes externally
  // We need to be careful not to overwrite user typing if the update is due to typing
  // But since inputAmount is the source of truth for the context, we should derive from it
  // unless we are currently editing.
  // However, for simplicity, we can just calculate the asset value from inputAmount
  // if inputAmount is updated.
  
  // Issue: Precision loss.
  // If User types "10" (Assets). Shares = 10 / Rate.
  // inputAmount = Shares.
  // If we calculate back: Assets = Shares * Rate.
  // It should be close to "10".

  // To handle "User typing" vs "External update" (like Max button),
  // we can compare the calculated asset value with the current display value.
  
  useEffect(() => {
    if (!inputAmount || !rate) {
      if (!inputAmount) setDisplayValue('');
      return;
    }

    const shares = new BigNumber(inputAmount);
    const assets = shares.times(rate);
    
    // If the difference is negligible, don't update to avoid cursor jumping or weird rounding
    // But since we don't have the cursor position here, we just update if it's significantly different
    // or if the user is not typing (hard to know).
    
    // Better approach: Only update displayValue if the deviation is significant
    // OR if the inputAmount was set by something else (like Max button).
    // Since we can't easily know the source, we'll just update.
    // To prevent loops, we can check if the new display value is close to the old one.
    
    const currentDisplay = new BigNumber(displayValue || '0');
    if (!assets.isEqualTo(currentDisplay)) {
        // Simple check: if they are very close (due to float math), don't update
        // But BigNumber should be precise enough if we handle decimals right.
        // Let's just format it to a reasonable number of decimals.
        // If the user typed "10", we want "10".
        // If shares * rate = 9.99999999, we might want "10".
        
        // Let's try to trust the user input if it matches the shares.
        // But here we are in useEffect, we don't know the user input history.
        
        // Let's just set it.
        setDisplayValue(assets.decimalPlaces(6).toString()); 
        // 6 decimals for display seems reasonable for tokens? 
        // Or use inputToken.decimals?
    }
  }, [inputAmount, rate]);

  const handleInput = (val: string) => {
    setDisplayValue(val);
    setAmountMode('exactIn');
    
    if (!val || !rate) {
      setInputAmount('');
      return;
    }

    const assets = new BigNumber(val);
    const shares = assets.div(rate);
    
    setInputAmount(shares.toFixed());
  };

  const usdValue = useMemo(() => {
    if (!price || !displayValue) return 0;
    const displayAmount = new BigNumber(displayValue);
    if (displayAmount.isNaN()) return 0;
    return displayAmount.times(price);
  }, [price, displayValue]);

  return (
    <Field.Root
      background="#222222"
      gap="12px"
    >
      <Flex justifyContent="space-between">
        <Field.Context>
          {(context) => (
            <Field.Label
              textStyle="text.small"
              color={context.invalid ? 'primary.red' : 'greyWhite.lightGrey3'}
            >
              {t('common.Pay Total')} (LVMON)
            </Field.Label>
          )}
        </Field.Context>
      </Flex>
      <Flex alignItems="center" gap="4px" justifyContent="space-between">
        <Box flex={1}>
          <Flex alignItems="center" justifyContent="flex-start">
            <Field.Input
              placeholder="0.0"
              textStyle="text.xlarge"
              fontWeight="bold"
              fontSize={20}
              w="full"
              caretColor="price.priceUp"
              value={displayValue}
              onInput={(e) => handleInput(e.currentTarget.value)}
              type="number"
              data-cy="ExchangeInput"
            />
          </Flex>
        </Box>
        <NoSSR>
          <InputToken
            setToken={setInputToken}
            token={outputToken}
            payableTokens={inputTokens}
          />
        </NoSSR>
      </Flex>
      <Flex justifyContent="space-between">
        <Field.Label
          alignItems="center"
          display="flex"
          gap="4px"
          textStyle="text.small"
          color="greyWhite.lightGrey3"
        >
          <AmountUsd
            textStyle="text.xsmall"
            color="greyWhite.lightGrey4"
            value={usdValue}
            opts={{
              prefix: '~$',
              displayDecimals: GENERIC_DISPLAY_DECIMALS,
            }}
            data-cy="ExchangeInputEst-Usd"
            data-cy-value={usdValue?.toString?.() || 0}
          />
        </Field.Label>
        <Field.Label
          alignItems="center"
          display="flex"
          gap="4px"
          textStyle="text.small"
          color="greyWhite.lightGrey3"
        >
          <Amount
            value={balanceInAssets}
            _hover={{
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            opts={{
              prefix: 'Balance: ',
              saveDecimalsMode: true,
            }}
            onClick={() => {
              if (!balance) return;
              setAmountMode('exactIn');
              // Max balance (Shares)
              const decimals = inputToken?.decimals ?? 18;
              const maxAmount = balance
                .decimalPlaces(decimals, BigNumber.ROUND_DOWN)
                .toFixed();
              setInputAmount(maxAmount);
              // The useEffect will update displayValue to Assets
            }}
            role="button"
            tabIndex={0}
            textDecoration="none"
            data-cy="TokenBalance"
          />
        </Field.Label>
      </Flex>
    </Field.Root>
  );
};
